import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import toast from 'react-hot-toast';

function SignupForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    requestedDrivers: '5',
    hearAboutUs: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (parseInt(formData.requestedDrivers) < 1) {
      toast.error('Number of drivers must be at least 1');
      return;
    }

    setSubmitting(true);
    const loadingToast = toast.loading('Submitting your request...');

    try {
      // Create signup request in Firestore
      await addDoc(collection(db, 'signupRequests'), {
        companyName: formData.companyName.trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim() || null,
        requestedDrivers: parseInt(formData.requestedDrivers),
        hearAboutUs: formData.hearAboutUs || null,
        message: formData.message.trim() || null,
        status: 'pending',
        createdAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
        rejectionReason: null,
        companyId: null,
        invitationId: null
      });

      toast.dismiss(loadingToast);
      toast.success('Request submitted successfully!');

      // Redirect to confirmation page
      setTimeout(() => {
        navigate('/signup-confirmation');
      }, 1000);

    } catch (error) {
      console.error('Error submitting signup request:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to submit request. Please try again or contact us directly.');
      setSubmitting(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#FAFAF9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      maxWidth: '560px',
      width: '100%',
      boxShadow: '0 4px 24px rgba(28, 25, 23, 0.08)',
      border: '1px solid #E7E5E4'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '24px'
    },
    logoText: {
      fontSize: '32px',
      fontWeight: '600',
      color: '#166534',
      marginBottom: '8px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#292524',
      marginBottom: '8px',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '15px',
      color: '#78716C',
      marginBottom: '24px',
      textAlign: 'center',
      lineHeight: '1.6'
    },
    benefitsBox: {
      backgroundColor: '#FAFAF9',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '24px',
      border: '1px solid #E7E5E4'
    },
    benefitsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    benefitItem: {
      fontSize: '14px',
      color: '#57534E',
      marginBottom: '6px',
      paddingLeft: '24px',
      position: 'relative'
    },
    checkIcon: {
      position: 'absolute',
      left: 0,
      color: '#166534'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '18px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    inputLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#44403C'
    },
    required: {
      color: '#991B1B'
    },
    input: {
      padding: '12px 14px',
      fontSize: '15px',
      border: '1px solid #D6D3D1',
      borderRadius: '8px',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      fontFamily: 'inherit',
      backgroundColor: '#FAFAF9',
      color: '#292524'
    },
    select: {
      padding: '12px 14px',
      fontSize: '15px',
      border: '1px solid #D6D3D1',
      borderRadius: '8px',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      fontFamily: 'inherit',
      backgroundColor: '#FAFAF9',
      color: '#292524',
      cursor: 'pointer'
    },
    textarea: {
      padding: '12px 14px',
      fontSize: '15px',
      border: '1px solid #D6D3D1',
      borderRadius: '8px',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      fontFamily: 'inherit',
      backgroundColor: '#FAFAF9',
      color: '#292524',
      minHeight: '100px',
      resize: 'vertical'
    },
    helpText: {
      fontSize: '12px',
      color: '#78716C',
      marginTop: '4px'
    },
    button: {
      padding: '14px 24px',
      fontSize: '15px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#166534',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.2s, box-shadow 0.2s',
      marginTop: '8px',
      boxShadow: '0 2px 8px rgba(22, 101, 52, 0.15)'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    loginLink: {
      textAlign: 'center',
      marginTop: '20px',
      fontSize: '14px',
      color: '#78716C'
    },
    link: {
      color: '#166534',
      textDecoration: 'none',
      fontWeight: '500',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <div style={styles.logoText}>SkipEye</div>
        </div>

        <div style={styles.title}>Start Your Free Trial</div>
        <div style={styles.subtitle}>
          Join waste management companies already saving time and improving efficiency with SkipEye
        </div>

        <div style={styles.benefitsBox}>
          <ul style={styles.benefitsList}>
            <li style={styles.benefitItem}>
              <span style={styles.checkIcon}>✓</span>
              14-day free trial, no credit card required
            </li>
            <li style={styles.benefitItem}>
              <span style={styles.checkIcon}>✓</span>
              Real-time job tracking and driver management
            </li>
            <li style={styles.benefitItem}>
              <span style={styles.checkIcon}>✓</span>
              Free onboarding call with our team
            </li>
            <li style={styles.benefitItem}>
              <span style={styles.checkIcon}>✓</span>
              Cancel anytime, no commitments
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>
              Company Name <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              style={styles.input}
              placeholder="Your Waste Management Ltd"
              required
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>
              Email Address <span style={styles.required}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              placeholder="you@yourcompany.com"
              required
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            />
            <div style={styles.helpText}>
              We'll send your account setup instructions to this email
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Phone Number (Optional)</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
              placeholder="+44 7700 900000"
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>
              Number of Drivers <span style={styles.required}>*</span>
            </label>
            <select
              name="requestedDrivers"
              value={formData.requestedDrivers}
              onChange={handleChange}
              style={styles.select}
              required
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            >
              <option value="1">1 driver</option>
              <option value="2">2 drivers</option>
              <option value="3">3 drivers</option>
              <option value="5">5 drivers</option>
              <option value="10">10 drivers</option>
              <option value="15">15 drivers</option>
              <option value="20">20+ drivers</option>
            </select>
            <div style={styles.helpText}>
              How many drivers will be using the system?
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>How did you hear about us?</label>
            <select
              name="hearAboutUs"
              value={formData.hearAboutUs}
              onChange={handleChange}
              style={styles.select}
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            >
              <option value="">-- Please select --</option>
              <option value="google">Google Search</option>
              <option value="referral">Referral from a friend/colleague</option>
              <option value="linkedin">LinkedIn</option>
              <option value="facebook">Facebook</option>
              <option value="industry-event">Industry Event</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Additional Information (Optional)</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="Tell us about your business or any specific requirements..."
              disabled={submitting}
              onFocus={(e) => { e.target.style.borderColor = '#166534'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#D6D3D1'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(submitting ? styles.buttonDisabled : {})
            }}
            disabled={submitting}
            onMouseOver={(e) => {
              if (!submitting) {
                e.target.style.backgroundColor = '#14532D';
                e.target.style.boxShadow = '0 4px 12px rgba(22, 101, 52, 0.25)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#166534';
              e.target.style.boxShadow = '0 2px 8px rgba(22, 101, 52, 0.15)';
            }}
          >
            {submitting ? 'Submitting Request...' : 'Start Free Trial'}
          </button>
        </form>

        <div style={styles.loginLink}>
          Already have an account?{' '}
          <span
            style={styles.link}
            onClick={() => navigate('/')}
            onMouseOver={(e) => { e.target.style.textDecoration = 'underline'; e.target.style.color = '#14532D'; }}
            onMouseOut={(e) => { e.target.style.textDecoration = 'none'; e.target.style.color = '#166534'; }}
          >
            Sign in here
          </span>
        </div>
      </div>
    </div>
  );
}

export default SignupForm;
